<template>
  <b-modal
    :ref="(parentSheet) ? 'newSubsheetModal' : 'newSpreadsheetModal'"
    hide-footer
    centered
    :title="(parentSheet) ? 'Add spread sheet' : 'Create spread sheet'"
    >
    <b-form @submit.prevent="createSheet" novalidate>
      <b-form-group
        label="Spreadsheet title:"
        >
        <b-form-input
          type="text"
          autocomplete="off"
          v-model.trim="$v.title.$model"
          :class="{ 'is-invalid': $v.title.$error }"
          :disabled="inProgress"
          />
        <b-form-invalid-feedback
          v-if="!$v.title.required"
          >
          Spreadsheet title is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        description="By this key you can refer to this table value from another table."
        >
        <template slot="label">
          <span>
            Spreadsheet key:
            <strong v-if="parentSheetKey && key">
              &{{ parentSheetKey }}.{{ key }}
            </strong>
          </span>
        </template>
        <b-form-input
          type="text"
          autocomplete="off"
          v-model.trim="$v.key.$model"
          :class="{ 'is-invalid': $v.key.$error }"
          :disabled="inProgress"
          />
        <b-form-invalid-feedback v-if="!$v.key.required">Spreadsheet key is required</b-form-invalid-feedback>
        <b-form-invalid-feedback v-if="!$v.key.noSpace">Spreadsheet key must be as single word</b-form-invalid-feedback>
        <b-form-invalid-feedback v-if="!$v.key.isUnique">Spreadsheet with the same key already exists</b-form-invalid-feedback>
      </b-form-group>
      <div v-if="!excelDataProvided">
        <b-form-group
          label="Excel form data(optional):"
          >
          <b-form-input
            type="text"
            autocomplete="off"
            placeholder="Paste excel form data here..."
            @paste.native="parseClipboard($event)"
            />
        </b-form-group>
        <b-form-file v-model="excelFile" placeholder="Choose a file..."></b-form-file>
        <br>
        <br>
      </div>
      <b-alert
        variant="success"
        dismissible
        :show="excelDataProvided"
        @dismissed="resetExcelData"
        >
        Excel data provided.
      </b-alert>
      <b-form-group v-if="!parentSheet" label="Store data in firebase">
        <b-form-radio-group v-model="storeInFB">
          <b-form-radio :value="true">yes</b-form-radio>
          <b-form-radio :value="false">no</b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <b-button type="submit" :disabled="inProgress">Create</b-button>
    </b-form>
  </b-modal>
</template>

<script>
// services
import db from '@/services/db'
// modules
import { mapState } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'
// helpers
import customValidation from '@/helpers/custom-validation'
import eventBus from '@/helpers/event-bus'

const $ = window.$
const noSpace = customValidation.noSpace

export default {
  props: ['parentSheet'],
  data () {
    return {
      inProgress: false,
      title: '',
      key: '',
      excelData: {},
      excelDataProvided: false,
      excelFile: null,
      sheets: {},
      storeInFB: true,
      gsOptions: {}
    }
  },
  watch: {
    excelFile () {
      if (!this.excelFile) return
      this.$readExcelFile(this.excelFile, (result) => {
        this.excelDataProvided = true
        this.excelData = result
      })
    }
  },
  computed: {
    ...mapState([
      'currentTab',
      'dashboardSheets'
    ]),
    parentSheetKey () {
      if (this.parentSheet) {
        return this.parentSheet.key
      }
      return null
    }
  },
  methods: {
    createSheet () {
      if (this.inProgress) return
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.inProgress = true
      let vm = this

      this.$store.commit('addProcess', {
        process: 'newSheet',
        message: 'Creating'
      })

      let callback = () => {
        this.$store.commit('deleteProcess', 'newSheet')
        this.inProgress = false
        if (this.$refs.newSpreadsheetModal) {
          this.$refs.newSpreadsheetModal.hide()
        }
        this.resetExcelData()
      }

      if (this.parentSheet) {
        db.sheets.addSubsheet(
          this.parentSheetKey,
          {
            key: vm.key,
            title: vm.title,
            excelData: vm.excelData
          },
          callback
        )
        this.title = ''
        this.key = ''
        this.$nextTick(() => {
          this.$v.$reset()
        })
      } else {
        const newSheetData = {
          key: vm.key,
          title: vm.title,
          excelData: vm.excelData,
          isLocal: !vm.storeInFB,
          gsOptions: this.gsOptions
        }

        if (!this.parentSheet) newSheetData.isLocal = !this.storeInFB

        db.sheets.create(
          newSheetData,
          this.currentTab,
          callback
        )
        this.title = ''
        this.key = ''
        this.$nextTick(() => {
          this.$v.$reset()
        })
      }
    },
    parseClipboard (e) {
      this.$excelFromClipboard(e, (result) => {
        this.excelData = result
        this.excelDataProvided = true
      })
    },
    resetExcelData () {
      this.excelDataProvided = false
      this.excelData = {}
    }
  },
  validations: {
    title: {
      required,
      minLength: minLength(1)
    },
    key: {
      required,
      minLength: minLength(1),
      noSpace,
      isUnique (val) {
        let prefix = (this.parentSheet) ? this.parentSheet.key + '.' : ''
        if (~this.dashboardSheets.indexOf(prefix + this.key)) return false
        return true
      }
    }
  },
  created () {
    eventBus.$on('addSpreadsheet', ({ gsOptions }) => {
      if (this.$refs.newSpreadsheetModal) {
        this.gsOptions = gsOptions;
        this.$refs.newSpreadsheetModal.show()
      }
    })
    eventBus.$on('addSubsheet', () => {
      if (this.$refs.newSubsheetModal) this.$refs.newSubsheetModal.show()
    })
  },
  mounted () {
    if (this.$refs.newSpreadsheetModal) {
      $(this.$refs.newSpreadsheetModal['$el']).appendTo('body')
    }
    if (this.$refs.newSubsheetModal) {
      $(this.$refs.newSubsheetModal['$el']).appendTo('body')
    }
  },
  beforeDestroy () {
    if (this.$refs.newSpreadsheetModal) {
      $(this.$refs.newSpreadsheetModal['$el']).remove()
    }
    if (this.$refs.newSubsheetModal) {
      $(this.$refs.newSubsheetModal['$el']).remove()
    }
  }
}
</script>
